import React, { useState } from "react";
import { motion } from "framer-motion";
import { Reveal, Kicker } from "@/components/Reveal";
import { BRAND_BLOGS, BLOGS_LABEL } from "@/data/blogs";

/**
 * Renders a brand's blog posts: video / image spotlight on one side,
 * bilingual title + paragraphs on the other. Expands/collapses full body.
 */
const BrandBlog = ({ slug, lang, accent = "#6A1E27" }) => {
  const posts = BRAND_BLOGS[slug];
  if (!posts || posts.length === 0) return null;

  return (
    <section data-testid={`brand-blog-${slug}`} className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-14">
          <Reveal>
            <Kicker>{BLOGS_LABEL.kicker[lang]}</Kicker>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-[#1A1A1A]">
              {BLOGS_LABEL.title[lang]}
            </h2>
          </Reveal>
        </div>

        <div className="space-y-20">
          {posts.map((post, i) => (
            <BlogPost key={post.id} post={post} lang={lang} accent={accent} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

const BlogPost = ({ post, lang, accent, index }) => {
  const [expanded, setExpanded] = useState(false);
  const paragraphs = post.body[lang];
  const visible = expanded ? paragraphs : paragraphs.slice(0, 2);
  const reverse = index % 2 === 1;

  return (
    <Reveal>
      <article
        data-testid={`blog-post-${post.id}`}
        className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
      >
        <div className={`lg:col-span-7 ${reverse ? "lg:order-2" : ""}`}>
          {post.video ? (
            <div className="relative overflow-hidden bg-black aspect-[9/16] md:aspect-[4/5] max-h-[640px]">
              <video
                data-testid={`blog-video-${post.id}`}
                src={post.video}
                controls
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
              />
            </div>
          ) : post.image ? (
            <div className="relative overflow-hidden">
              <img src={post.image} alt="" className="w-full h-full object-cover" />
            </div>
          ) : null}
        </div>

        <div className={`lg:col-span-5 ${reverse ? "lg:order-1" : ""}`}>
          {post.date && (
            <span className="text-xs uppercase tracking-[0.26em]" style={{ color: accent }}>
              {post.date}
            </span>
          )}
          <h3 className="font-display text-3xl md:text-4xl text-[#1A1A1A] mt-3 mb-5 leading-tight">
            {post.title[lang]}
          </h3>
          <div className="space-y-4 text-[#4A4A4A] leading-relaxed">
            {visible.map((p, idx) => (
              <p key={idx} className="text-base md:text-lg">
                {p}
              </p>
            ))}
          </div>
          {paragraphs.length > 2 && (
            <motion.button
              whileTap={{ scale: 0.97 }}
              data-testid={`blog-toggle-${post.id}`}
              onClick={() => setExpanded((v) => !v)}
              className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em]"
              style={{ color: accent }}
            >
              {expanded ? BLOGS_LABEL.readLess[lang] : BLOGS_LABEL.readMore[lang]}
              <span>{expanded ? "−" : "+"}</span>
            </motion.button>
          )}
        </div>
      </article>
    </Reveal>
  );
};

export default BrandBlog;
